import { Component, OnInit, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import {
  CrmService,
  EmbudoDto,
  ColumnaTablero,
  CasoDto,
  FichaContacto,
  CitaClinica,
  ActividadDto,
  UsuarioDto,
} from '../core/crm.service';

interface ProcedimientoCatalogo {
  codigo: string;
  nombre: string;
  categoria: 'Quirúrgico' | 'No Quirúrgico' | 'Diagnóstico' | 'Laser / Especial';
  ojo: string;
  convenioBase: string;
  slaHoras: number;
  tarifaRef: string;
  requiereBiometria: boolean;
  embudoClave: string;
}

@Component({
  selector: 'app-tablero',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="studio-board-workspace">
      <!-- 1. CABECERA EJECUTIVA UNIFICADA (ESTILO CREATIO 8 STUDIO) -->
      <section class="studio-command-card">
        <!-- Fila superior: Pestañas de procesos con subrayado fino y conteo en vivo -->
        <div class="studio-proc-tabs-bar">
          <div class="studio-proc-tabs" role="tablist">
            @for (e of embudosOrdenados(); track e.id) {
              <button
                type="button"
                role="tab"
                class="studio-proc-tab"
                [class.active]="embudoSeleccionado()?.id === e.id"
                (click)="seleccionarEmbudo(e)"
              >
                <span class="spt-name">{{ e.nombre }}</span>
                <span class="spt-count">({{ contarCasosEmbudo(e) }})</span>
              </button>
            }
          </div>

          <div class="studio-proc-actions">
            <div class="studio-mode-pills">
              <button
                type="button"
                class="smp-btn"
                [class.on]="modo() === 'mesa'"
                (click)="modo.set('mesa')"
              >
                Mesa Rápida
              </button>
              <button
                type="button"
                class="smp-btn"
                [class.on]="modo() === 'kanban'"
                (click)="modo.set('kanban')"
              >
                Tablero Studio
              </button>
              <button
                type="button"
                class="smp-btn"
                [class.on]="modo() === 'lista'"
                (click)="modo.set('lista')"
              >
                Matriz Ejecutiva
              </button>
              <button
                type="button"
                class="smp-btn"
                [class.on]="modo() === 'procedimientos'"
                (click)="modo.set('procedimientos')"
              >
                Catálogo Procedimientos
              </button>
            </div>

            <button type="button" class="studio-new-case-btn" (click)="nuevoCasoRapido()">
              + Nuevo caso
            </button>
          </div>
        </div>

        <!-- Fila inferior: 4 KPIs ejecutivos + Filtros integrados a la derecha -->
        <div class="studio-kpi-filter-row">
          <div class="studio-kpis">
            <div class="studio-kpi-item">
              <div class="skpi-val">{{ todosLosCasosFiltrados().length }}</div>
              <div class="skpi-lbl">Casos activos</div>
            </div>
            <div class="studio-kpi-divider"></div>
            <div class="studio-kpi-item">
              <div class="skpi-val">{{ porcentajeCumplimientoSla() }}%</div>
              <div class="skpi-lbl">Atendidos a tiempo</div>
            </div>
            <div class="studio-kpi-divider"></div>
            <div class="studio-kpi-item">
              <div class="skpi-val">{{ tiempoMedioDias() }} días</div>
              <div class="skpi-lbl">Tiempo medio</div>
            </div>
            <div class="studio-kpi-divider"></div>
            <div
              class="studio-kpi-item clickable"
              [class.kpi-filtered]="filtroSla() === 'urgentes'"
              (click)="alternarFiltroUrgentes()"
              title="Clic para filtrar casos prioritarios o por vencer"
            >
              <div class="skpi-val">{{ citasHoyCount() }}</div>
              <div class="skpi-lbl">Citas hoy ({{ urgentesCount() }} prioritarios)</div>
            </div>
          </div>

          <div class="studio-filters-panel">
            <div class="sf-grid">
              <div class="sf-field">
                <label class="sf-lbl">Estado:</label>
                <select
                  class="sf-select"
                  [ngModel]="estadoFiltro()"
                  (ngModelChange)="cambiarEstadoFiltro($event)"
                >
                  <option value="abierto">Abiertos</option>
                  <option value="ganado">Completados</option>
                  <option value="perdido">Cancelados</option>
                  <option value="todos">Todos</option>
                </select>
              </div>

              <div class="sf-field">
                <label class="sf-lbl">Convenio:</label>
                <select
                  class="sf-select"
                  [ngModel]="filtroEtiqueta()"
                  (ngModelChange)="filtroEtiqueta.set($event)"
                >
                  <option value="">Todos</option>
                  @for (tag of etiquetasSugeridas; track tag) {
                    <option [value]="tag">{{ tag }}</option>
                  }
                </select>
              </div>

              <div class="sf-field">
                <label class="sf-lbl">Red / Canal:</label>
                <select
                  class="sf-select"
                  [ngModel]="filtroCanal()"
                  (ngModelChange)="filtroCanal.set($event)"
                >
                  <option value="">Todas las redes</option>
                  <option value="comentarios">📢 Solo Comentarios / Reseñas</option>
                  <option value="whatsapp">WhatsApp Business</option>
                  <option value="instagram">Instagram (DM y Reels)</option>
                  <option value="messenger">Facebook / Messenger</option>
                  <option value="tiktok">TikTok (DM y Videos)</option>
                  <option value="youtube">YouTube (Comentarios)</option>
                  <option value="google">Google Maps (Reseñas)</option>
                  <option value="pbx">PBX / Web</option>
                </select>
              </div>

              <div class="sf-field">
                <label class="sf-lbl">Responsable:</label>
                <select
                  class="sf-select"
                  [ngModel]="filtroResponsable()"
                  (ngModelChange)="filtroResponsable.set($event)"
                >
                  <option value="">Todos</option>
                  @for (u of usuarios(); track u.id) {
                    <option [value]="u.id">{{ u.nombre }}</option>
                  }
                </select>
              </div>
            </div>

            <div class="sf-search-row">
              <div class="sf-search-wrap">
                <svg class="sf-search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  type="search"
                  class="sf-search-input"
                  placeholder="Buscar paciente, procedimiento o etiqueta..."
                  [ngModel]="filtroTexto()"
                  (ngModelChange)="filtroTexto.set($event)"
                />
              </div>

              @if (hayFiltros()) {
                <button type="button" class="sf-clear-btn" (click)="limpiarFiltros()">
                  ✕ Limpiar filtros
                </button>
              }
            </div>
          </div>
        </div>
      </section>

      <!-- 2. BARRA EJECUTIVA DE ETAPAS CONECTADAS (VISIBLE COMPLETA SIN SCROLL HORIZONTAL) -->
      @if (modo() !== 'procedimientos') {
        <section class="studio-chevron-card">
          <div class="chevron-pipeline-track">
            <button
              type="button"
              class="chevron-step chevron-all"
              [class.selected]="etapaFiltroId() === ''"
              (click)="etapaFiltroId.set('')"
              title="Mostrar todas las etapas del proceso"
            >
              <span class="chv-title">Todas las etapas</span>
              <span class="chv-sub">({{ todosLosCasosSinEtapaFiltro().length }})</span>
            </button>

            @for (col of columnasFiltradas(); track col.etapa.id; let idx = $index) {
              <button
                type="button"
                class="chevron-step"
                [class.selected]="etapaFiltroId() === col.etapa.id"
                [class.empty-stage]="col.casos.length === 0"
                (click)="etapaFiltroId.set(etapaFiltroId() === col.etapa.id ? '' : col.etapa.id)"
              >
                <span class="chv-title">{{ col.etapa.orden }}. {{ nombreEtapaLimpio(col.etapa.nombre) }}</span>
                <span class="chv-sub">({{ col.casos.length }})</span>
              </button>
            }
          </div>
        </section>
      }

      @if (avisoAccion()) {
        <div class="mesa-toast-banner">
          <span>{{ avisoAccion() }}</span>
          <button type="button" class="mesa-toast-close" (click)="avisoAccion.set('')">✕</button>
        </div>
      }

      <!-- 3. MODO MESA RÁPIDA EN 1 CLIC CON RESPUESTA EN VIVO EN CADA TARJETA (TODAS LAS REDES Y COMENTARIOS) -->
      @if (modo() === 'mesa') {
        <div class="mesa-workbench-list">
          @for (caso of todosLosCasosFiltrados(); track caso.id) {
            @let infoRed = obtenerInfoRedCaso(caso);
            <article class="mesa-card" [attr.data-sla]="nivelSla(caso)">
              <!-- COLUMNA 1: DATOS DEL PACIENTE, RED SOCIAL, PROCEDIMIENTO Y TIEMPO -->
              <div class="mesa-col-patient" (click)="abrirInspector(caso)">
                <div class="mesa-patient-top">
                  <div class="mesa-avatar">{{ iniciales(caso.contacto) }}</div>
                  <div class="mesa-patient-meta">
                    <div class="mesa-pname-row">
                      <span class="mesa-pname">{{ caso.contacto }}</span>
                      <span class="spcard-channel" [attr.data-ch]="infoRed.redClave">
                        {{ infoRed.redNombre }}
                      </span>
                    </div>
                    <div class="mesa-proc-title">{{ caso.titulo }}</div>
                  </div>
                </div>

                <div class="mesa-tags-row">
                  <span class="spcard-tag-mini" [attr.data-color]="infoRed.esComentario ? 'amber' : 'blue'">
                    {{ infoRed.esComentario ? '📢 ' + infoRed.subtipo : '💬 ' + infoRed.subtipo }}
                  </span>
                  <span class="spcard-conv-main">{{ obtenerConvenioPrincipal(caso) }}</span>
                  @for (tag of obtenerEtiquetasSecundarias(caso); track tag) {
                    <span class="spcard-tag-mini" [attr.data-color]="colorEtiqueta(tag)">{{ tag }}</span>
                  }
                  <span class="mesa-stage-badge">
                    Etapa {{ ordenActual(caso) }}: {{ nombreEtapaLimpio(caso.etapa) }}
                  </span>
                </div>

                <div class="mesa-sla-footer">
                  <div class="spcard-sla-top">
                    <span class="spcard-sla-lbl">
                      Responsable: <b>{{ caso.responsable || 'Coordinación Qx' }}</b>
                    </span>
                    <span class="spcard-sla-status" [attr.data-sla]="nivelSla(caso)">
                      {{ textoEstadoSla(caso) }}
                    </span>
                  </div>
                  <div class="spcard-sla-segments">
                    <span class="sla-seg" [class.on]="porcentajeSlaCaso(caso) >= 25" [attr.data-tone]="nivelSla(caso)"></span>
                    <span class="sla-seg" [class.on]="porcentajeSlaCaso(caso) >= 50" [attr.data-tone]="nivelSla(caso)"></span>
                    <span class="sla-seg" [class.on]="porcentajeSlaCaso(caso) >= 75" [attr.data-tone]="nivelSla(caso)"></span>
                    <span class="sla-seg" [class.on]="porcentajeSlaCaso(caso) >= 92" [attr.data-tone]="nivelSla(caso)"></span>
                  </div>
                </div>
              </div>

              <!-- COLUMNA 2: LECTURA DE COMENTARIO / CHAT DE RED SOCIAL Y CAJA DE RESPUESTA EN VIVO -->
              <div class="mesa-col-chat" (click)="$event.stopPropagation()">
                <div class="mesa-chat-feed">
                  <div class="mesa-msg-in">
                    <span class="mesa-msg-author">
                      {{ infoRed.esComentario ? '📢 ' + infoRed.subtipo + ' (' + infoRed.redNombre + ')' : '💬 ' + infoRed.redNombre }}:
                    </span>
                    <span class="mesa-msg-text">{{ ultimoMensajePaciente(caso) }}</span>
                  </div>

                  @for (resp of historialRespuestasCaso(caso); track $index) {
                    <div class="mesa-msg-out">
                      <span class="mesa-msg-author">{{ resp.autor }} · {{ resp.hora }}:</span>
                      <span class="mesa-msg-text">{{ resp.texto }}</span>
                    </div>
                  }
                </div>

                <div class="mesa-quick-chips">
                  <span class="mesa-chip-lbl">1 clic:</span>
                  @if (infoRed.esComentario) {
                    <button
                      type="button"
                      class="mesa-tpl-chip"
                      (click)="usarPlantillaRapidaTarjeta(caso, 'comentario_dm')"
                    >
                      📢 Responder comentario + Enviar DM
                    </button>
                  }
                  <button
                    type="button"
                    class="mesa-tpl-chip"
                    (click)="usarPlantillaRapidaTarjeta(caso, 'valoracion')"
                  >
                    📅 Confirmar valoración
                  </button>
                  <button
                    type="button"
                    class="mesa-tpl-chip"
                    (click)="usarPlantillaRapidaTarjeta(caso, 'orden')"
                  >
                    📄 Pedir orden médica
                  </button>
                  <button
                    type="button"
                    class="mesa-tpl-chip"
                    (click)="usarPlantillaRapidaTarjeta(caso, 'preparacion')"
                  >
                    📋 Preparación cirugía
                  </button>
                </div>

                <div class="mesa-reply-bar">
                  <select
                    class="mesa-stage-select"
                    style="width: 152px; flex-shrink: 0; height: 32px;"
                    [ngModel]="obtenerCanalRespuestaCaso(caso)"
                    (ngModelChange)="actualizarCanalRespuestaCaso(caso.id, $event)"
                    title="Elegir red social o modalidad de respuesta"
                  >
                    <option [value]="infoRed.redNombre">Por {{ infoRed.redNombre }}</option>
                    <option value="Comentario + DM">📢 Comentario + DM</option>
                    <option value="WhatsApp">💬 WhatsApp</option>
                    <option value="Instagram DM">📸 Instagram DM</option>
                    <option value="Messenger">📘 Messenger</option>
                    <option value="TikTok">🎵 TikTok</option>
                    <option value="YouTube">▶️ YouTube</option>
                    <option value="Google Maps">⭐ Google Maps</option>
                  </select>
                  <input
                    type="text"
                    class="mesa-reply-input"
                    placeholder="Responde aquí mismo al comentario o chat de red social del paciente..."
                    [ngModel]="obtenerTextoRespuesta(caso.id)"
                    (ngModelChange)="actualizarTextoRespuesta(caso.id, $event)"
                    (keyup.enter)="responderEnTarjeta(caso)"
                  />
                  <button
                    type="button"
                    class="mesa-reply-send-btn"
                    (click)="responderEnTarjeta(caso)"
                  >
                    Responder ↵
                  </button>
                </div>
              </div>

              <!-- COLUMNA 3: CAMBIO DIRECTO DE ETAPA Y ACCIONES OPERATIVAS EN 1 CLIC -->
              <div class="mesa-col-actions" (click)="$event.stopPropagation()">
                <div class="mesa-stage-jump">
                  <label class="mesa-jump-lbl">Cambiar etapa directo:</label>
                  <select
                    class="mesa-stage-select"
                    [ngModel]="caso.etapaId"
                    (ngModelChange)="moverCaso(caso, $event)"
                  >
                    @for (col of columnas(); track col.etapa.id) {
                      <option [value]="col.etapa.id">
                        {{ col.etapa.orden }}. {{ nombreEtapaLimpio(col.etapa.nombre) }}
                      </option>
                    }
                  </select>
                </div>

                <div class="mesa-action-btns-grid">
                  <button
                    type="button"
                    class="mesa-act-btn whatsapp"
                    (click)="abrirWhatsAppCaso(caso)"
                    title="Responder en público + DM o abrir conversación completa en Chats y Redes"
                  >
                    {{ infoRed.esComentario ? '📢 Público + DM' : '💬 ' + infoRed.redNombre }}
                  </button>
                  <button
                    type="button"
                    class="mesa-act-btn warn"
                    (click)="accionRapidaTarjeta(caso, 'no_contesto')"
                    title="Marcar que no contestó y pasar a seguimiento"
                  >
                    📞 No contestó
                  </button>
                  <button
                    type="button"
                    class="mesa-act-btn info"
                    (click)="accionRapidaTarjeta(caso, 'autorizacion')"
                    title="Radicar validación de autorización EPS/Póliza"
                  >
                    🛡️ Autorización
                  </button>
                  <button
                    type="button"
                    class="mesa-act-btn primary"
                    (click)="accionRapidaTarjeta(caso, 'agendar')"
                    title="Confirmar y pasar a Programación de Cirugía"
                  >
                    📅 Agendar Qx
                  </button>
                </div>

                <div class="mesa-bottom-links">
                  <button type="button" class="mesa-link-btn" (click)="abrirInspector(caso)">
                    Ficha 360° y Citas →
                  </button>
                </div>
              </div>
            </article>
          } @empty {
            <div class="mesa-empty-state">
              <div class="scol-empty-title">No hay pacientes en esta etapa o red social</div>
              <div class="scol-empty-sub">Selecciona "Todas las etapas" o "Todas las redes" arriba.</div>
              <button type="button" class="studio-new-case-btn" style="margin-top: 8px;" (click)="nuevoCasoRapido()">
                + Nuevo caso clínico
              </button>
            </div>
          }
        </div>
      }

      <!-- 3A. MODO TABLERO STUDIO (COLUMNAS INTELIGENTES Y TARJETAS CLÍNICAS) -->
      @if (modo() === 'kanban') {
        <div class="studio-kanban-canvas">
          @for (col of columnasVisiblesKanban(); track col.etapa.id; let idx = $index) {
            <div
              class="studio-column"
              [class.drag-over]="columnaSobreId() === col.etapa.id"
              (dragover)="permitirSoltar($event, col.etapa.id)"
              (dragleave)="salirColumna(col.etapa.id)"
              (drop)="soltarEnColumna($event, col.etapa.id)"
            >
              <div class="scol-head">
                <div class="scol-title-wrap">
                  <span class="scol-dot" [style.background]="colorEtapa(idx)"></span>
                  <span class="scol-title" [title]="nombreEtapaLimpio(col.etapa.nombre)">
                    {{ col.etapa.orden }}. {{ nombreEtapaLimpio(col.etapa.nombre) }}
                  </span>
                  <span class="scol-badge">{{ col.casos.length }}</span>
                </div>

                <div class="scol-actions">
                  <button
                    type="button"
                    class="scol-icon-btn"
                    title="Registrar caso en esta etapa"
                    (click)="nuevoCasoEnEtapa(col.etapa.id)"
                  >
                    +
                  </button>
                </div>
              </div>

              <div class="scol-subhead">
                <span>Flujo etapa {{ col.etapa.orden }} de {{ columnas().length }}</span>
                <span>Meta: {{ metaSlaEtapa(col.etapa.orden) }}</span>
              </div>

              <div class="scol-cards">
                @for (caso of col.casos; track caso.id) {
                  <article
                    class="studio-pcard"
                    [attr.data-sla]="nivelSla(caso)"
                    draggable="true"
                    (dragstart)="iniciarArrastre($event, caso)"
                    (click)="abrirInspector(caso)"
                  >
                    <div class="spcard-header">
                      <div class="spcard-patient-name">{{ caso.contacto }}</div>
                      <span class="spcard-channel" [attr.data-ch]="caso.origen || 'manual'">
                        {{ formatearCanal(caso.origen) }}
                      </span>
                    </div>

                    <div class="spcard-proc">{{ caso.titulo }}</div>

                    <div class="spcard-convenio-row">
                      <span class="spcard-conv-main">
                        {{ obtenerConvenioPrincipal(caso) }}
                      </span>
                      @for (tag of obtenerEtiquetasSecundarias(caso); track tag) {
                        <span class="spcard-tag-mini" [attr.data-color]="colorEtiqueta(tag)">
                          {{ tag }}
                        </span>
                      }
                    </div>

                    <!-- Barra segmentada Estado de Tiempo estilo Creatio 8 -->
                    <div class="spcard-sla-block">
                      <div class="spcard-sla-top">
                        <span class="spcard-sla-lbl">Estado de tiempo</span>
                        <span class="spcard-sla-status" [attr.data-sla]="nivelSla(caso)">
                          {{ textoEstadoSla(caso) }}
                        </span>
                      </div>
                      <div class="spcard-sla-segments">
                        <span
                          class="sla-seg"
                          [class.on]="porcentajeSlaCaso(caso) >= 25"
                          [attr.data-tone]="nivelSla(caso)"
                        ></span>
                        <span
                          class="sla-seg"
                          [class.on]="porcentajeSlaCaso(caso) >= 50"
                          [attr.data-tone]="nivelSla(caso)"
                        ></span>
                        <span
                          class="sla-seg"
                          [class.on]="porcentajeSlaCaso(caso) >= 75"
                          [attr.data-tone]="nivelSla(caso)"
                        ></span>
                        <span
                          class="sla-seg"
                          [class.on]="porcentajeSlaCaso(caso) >= 92"
                          [attr.data-tone]="nivelSla(caso)"
                        ></span>
                      </div>
                    </div>

                    <div class="spcard-footer" (click)="$event.stopPropagation()">
                      <span class="spcard-resp" [title]="caso.responsable || 'Coordinación Clínica'">
                        {{ caso.responsable || 'Coordinación Qx' }}
                      </span>

                      <div class="spcard-nav-btns">
                        @if (etapaAnteriorId(col.etapa.id); as antId) {
                          <button
                            type="button"
                            class="spcard-prev-btn"
                            title="Regresar a etapa anterior"
                            (click)="moverCaso(caso, antId)"
                          >
                            ←
                          </button>
                        }
                        @if (etapaSiguienteId(col.etapa.id); as sigId) {
                          <button
                            type="button"
                            class="spcard-advance-btn"
                            (click)="moverCaso(caso, sigId)"
                          >
                            Avanzar →
                          </button>
                        } @else {
                          <button
                            type="button"
                            class="spcard-advance-btn done"
                            (click)="marcarEstado(caso, 'ganado')"
                          >
                            ✓ Finalizar
                          </button>
                        }
                      </div>
                    </div>
                  </article>
                } @empty {
                  <div class="scol-empty-box">
                    <div class="scol-empty-title">Etapa despejada</div>
                    <div class="scol-empty-sub">Sin casos retenidos en {{ col.etapa.nombre }}</div>
                    <button
                      type="button"
                      class="scol-empty-btn"
                      (click)="nuevoCasoEnEtapa(col.etapa.id)"
                    >
                      + Registrar paciente aquí
                    </button>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }

      <!-- 3B. MODO MATRIZ EJECUTIVA (LISTA COMPACTA CREATIO 8) -->
      @if (modo() === 'lista') {
        <div class="studio-list-wrap">
          <div class="studio-table-card">
            <table class="studio-table">
              <thead>
                <tr>
                  <th>Paciente</th>
                  <th>Procedimiento / Solicitud Clínica</th>
                  <th>Convenio y Etiquetas</th>
                  <th>Etapa Actual del Proceso</th>
                  <th>Responsable</th>
                  <th>Canal</th>
                  <th>Estado de Tiempo</th>
                  <th style="text-align: right;">Acción</th>
                </tr>
              </thead>
              <tbody>
                @for (c of todosLosCasosFiltrados(); track c.id) {
                  <tr (click)="abrirInspector(c)">
                    <td>
                      <div class="st-patient-cell">
                        <div class="st-avatar">{{ iniciales(c.contacto) }}</div>
                        <span class="st-pname">{{ c.contacto }}</span>
                      </div>
                    </td>
                    <td class="st-proc-cell">{{ c.titulo }}</td>
                    <td>
                      <div class="st-tags-wrap">
                        <span class="spcard-conv-main">{{ obtenerConvenioPrincipal(c) }}</span>
                        @for (tag of obtenerEtiquetasSecundarias(c); track tag) {
                          <span class="spcard-tag-mini" [attr.data-color]="colorEtiqueta(tag)">{{ tag }}</span>
                        }
                      </div>
                    </td>
                    <td (click)="$event.stopPropagation()">
                      <select
                        class="studio-inline-select"
                        [ngModel]="c.etapaId"
                        (ngModelChange)="moverCaso(c, $event)"
                      >
                        @for (col of columnas(); track col.etapa.id) {
                          <option [value]="col.etapa.id">{{ col.etapa.orden }}. {{ col.etapa.nombre }}</option>
                        }
                      </select>
                    </td>
                    <td (click)="$event.stopPropagation()">
                      <select
                        class="studio-inline-select"
                        [ngModel]="c.responsableId || ''"
                        (ngModelChange)="asignarResponsable(c, $event)"
                      >
                        <option value="">Coordinación Qx</option>
                        @for (u of usuarios(); track u.id) {
                          <option [value]="u.id">{{ u.nombre }}</option>
                        }
                      </select>
                    </td>
                    <td>
                      <span class="spcard-channel" [attr.data-ch]="c.origen || 'manual'">
                        {{ formatearCanal(c.origen) }}
                      </span>
                    </td>
                    <td>
                      <span class="st-sla-pill" [attr.data-sla]="nivelSla(c)">
                        {{ textoEstadoSla(c) }}
                      </span>
                    </td>
                    <td style="text-align: right;" (click)="$event.stopPropagation()">
                      <button type="button" class="spcard-advance-btn" (click)="abrirInspector(c)">
                        Ficha 360° →
                      </button>
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="8" class="st-empty-row">
                      No se encontraron pacientes con los filtros seleccionados.
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }

      <!-- 3C. MODO CATÁLOGO Y CONTROL DE PROCEDIMIENTOS OFTALMOLÓGICOS (?vista=procedimientos) -->
      @if (modo() === 'procedimientos') {
        <div class="studio-list-wrap">
          <div class="proc-catalog-header">
            <div>
              <div class="pch-title">Catálogo Maestro de Procedimientos Oftalmológicos y Tiempos de Atención</div>
              <div class="pch-sub">
                Selecciona cualquier procedimiento para filtrar sus pacientes en el Tablero Studio o iniciar una nueva orden clínica.
              </div>
            </div>
            <div class="pch-pills">
              @for (cat of ['Todos', 'Quirúrgico', 'No Quirúrgico', 'Diagnóstico', 'Laser / Especial']; track cat) {
                <button
                  type="button"
                  class="pch-pill"
                  [class.on]="categoriaProcFiltro() === cat"
                  (click)="categoriaProcFiltro.set(cat)"
                >
                  {{ cat }}
                </button>
              }
            </div>
          </div>

          <div class="studio-table-card">
            <table class="studio-table">
              <thead>
                <tr>
                  <th>Código CUPS</th>
                  <th>Procedimiento Oftalmológico</th>
                  <th>Categoría</th>
                  <th>Ojo / Biometría</th>
                  <th>Convenio Frecuente</th>
                  <th>Tiempo Meta</th>
                  <th>Tarifa Ref.</th>
                  <th style="text-align: right;">Acción Operativa</th>
                </tr>
              </thead>
              <tbody>
                @for (p of procedimientosFiltrados(); track p.codigo) {
                  <tr>
                    <td><span class="cups-code">{{ p.codigo }}</span></td>
                    <td class="st-proc-cell">{{ p.nombre }}</td>
                    <td>
                      <span class="spcard-tag-mini" [attr.data-color]="p.categoria === 'Quirúrgico' ? 'blue' : (p.categoria === 'Diagnóstico' ? 'teal' : 'purple')">
                        {{ p.categoria }}
                      </span>
                    </td>
                    <td>
                      {{ p.ojo }}
                      @if (p.requiereBiometria) {
                        <span class="bio-req-badge">Requiere LIO/Biometría</span>
                      }
                    </td>
                    <td>{{ p.convenioBase }}</td>
                    <td><b>{{ p.slaHoras }} h</b></td>
                    <td>{{ p.tarifaRef }}</td>
                    <td style="text-align: right;">
                      <button
                        type="button"
                        class="spcard-advance-btn"
                        (click)="verProcedimientoEnTablero(p)"
                      >
                        Ver en Tablero →
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }

      <!-- INSPECTOR 360° DESLIZABLE DEL CASO Y PACIENTE -->
      @if (casoSeleccionado(); as sel) {
        <div class="inspector-backdrop" (click)="cerrarInspector()">
          <aside class="inspector-drawer" (click)="$event.stopPropagation()">
            <div class="inspector-hd">
              <div>
                <div class="eyebrow-proc">{{ sel.embudo }}</div>
                <div class="inspector-title">{{ sel.titulo }}</div>
              </div>
              <button type="button" class="modal-close" (click)="cerrarInspector()">✕</button>
            </div>

            <div class="inspector-body">
              <div class="insp-meta-grid">
                <div class="insp-meta-box">
                  <span class="insp-mini-lbl">ESTADO DEL CASO</span>
                  <div style="display: flex; gap: 6px; margin-top: 6px;">
                    <button
                      type="button"
                      class="status-chip"
                      [class.active-open]="sel.estado === 'abierto'"
                      (click)="marcarEstado(sel, 'abierto')"
                    >
                      En curso
                    </button>
                    <button
                      type="button"
                      class="status-chip ok"
                      [class.active-ok]="sel.estado === 'ganado'"
                      (click)="marcarEstado(sel, 'ganado')"
                    >
                      ✓ Completado
                    </button>
                    <button
                      type="button"
                      class="status-chip cancel"
                      [class.active-cancel]="sel.estado === 'perdido'"
                      (click)="marcarEstado(sel, 'perdido')"
                    >
                      ✕ Cancelado
                    </button>
                  </div>
                </div>

                <div class="insp-meta-box">
                  <span class="insp-mini-lbl">RESPONSABLE ASIGNADO</span>
                  <select
                    class="modal-input"
                    style="margin-top: 6px; height: 32px; font-size: 12px;"
                    [ngModel]="sel.responsableId || ''"
                    (ngModelChange)="asignarResponsable(sel, $event)"
                  >
                    <option value="">Sin responsable asignado</option>
                    @for (u of usuarios(); track u.id) {
                      <option [value]="u.id">{{ u.nombre }} ({{ u.rol }})</option>
                    }
                  </select>
                </div>
              </div>

              <div class="insp-block">
                <div class="insp-block-head"><span>CONVENIO Y ETIQUETAS CLÍNICAS</span></div>
                <div class="pcard-tags">
                  @for (tag of (sel.etiquetas || []); track tag) {
                    <span class="spcard-tag-mini" [attr.data-color]="colorEtiqueta(tag)">
                      {{ tag }}
                      <button type="button" class="tag-x-btn" (click)="quitarEtiquetaInspector(sel, tag)">×</button>
                    </span>
                  }
                </div>
                <div class="quick-tags-bar">
                  <span class="m">Agregar:</span>
                  @for (sug of etiquetasSugeridas; track sug) {
                    <button type="button" class="quick-tag-add" (click)="agregarEtiquetaInspector(sel, sug)">
                      + {{ sug }}
                    </button>
                  }
                </div>
              </div>

              <div class="insp-block">
                <div class="insp-block-head">
                  <span>ETAPA DEL PROCESO (CLIC PARA MOVER)</span>
                </div>
                <div class="insp-stages-grid">
                  @for (col of columnas(); track col.etapa.id) {
                    <button
                      type="button"
                      class="insp-stage-btn"
                      [class.done]="col.etapa.orden < ordenActual(sel)"
                      [class.current]="col.etapa.id === sel.etapaId"
                      (click)="moverCaso(sel, col.etapa.id)"
                    >
                      <span>{{ col.etapa.orden }}. {{ col.etapa.nombre }}</span>
                    </button>
                  }
                </div>
              </div>

              @if (fichaPaciente(); as ficha) {
                <div class="insp-patient-box">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                    <div style="display: flex; align-items: center; gap: 10px;">
                      <div class="st-avatar">{{ iniciales(sel.contacto) }}</div>
                      <div>
                        <div style="font-size: 13.5px; font-weight: 700; color: #162032;">
                          {{ ficha.contacto.nombres }} {{ ficha.contacto.apellidos }}
                        </div>
                        <div class="m">
                          {{ ficha.contacto.tipoDocumento || 'CC' }} {{ ficha.contacto.numeroDocumento || '—' }} · {{ ficha.contacto.telefono || 'Sin teléfono' }}
                        </div>
                      </div>
                    </div>
                    <button type="button" class="spcard-advance-btn" (click)="irAPaciente(ficha.contacto.id)">
                      Expediente completo →
                    </button>
                  </div>

                  <div class="citas-subbox">
                    <div style="font-size: 10.5px; font-weight: 700; color: #186ADE; margin-bottom: 4px;">
                      CITAS REGISTRADAS EN AGENDA CLÍNICA ({{ citasPaciente().length }})
                    </div>
                    @for (cita of citasPaciente(); track cita.fechaHora) {
                      <div class="cita-row">
                        <div>
                          <b>{{ cita.especialidad }}</b> · {{ cita.profesional }}
                          <div class="m">{{ cita.sede }} · {{ cita.fechaHora | date:'dd MMM yyyy, h:mm a' }}</div>
                        </div>
                        <span class="cita-badge" [attr.data-est]="cita.estado">{{ cita.estado | uppercase }}</span>
                      </div>
                    } @empty {
                      <div class="m">Sin citas clínicas registradas para este documento.</div>
                    }
                  </div>
                </div>

                <div class="insp-block">
                  <div class="insp-block-head"><span>REGISTRAR SEGUIMIENTO O AUTORIZACIÓN</span></div>
                  <div style="display: flex; gap: 8px;">
                    <input
                      type="text"
                      class="modal-input"
                      style="flex: 1; height: 32px; font-size: 12px;"
                      placeholder="Escribe la gestión, número de autorización o respuesta..."
                      [(ngModel)]="nuevaNotaTexto"
                      (keyup.enter)="guardarNota(sel)"
                    />
                    <button type="button" class="studio-new-case-btn" (click)="guardarNota(sel)">Guardar</button>
                  </div>
                </div>

                <div class="insp-block">
                  <div class="insp-block-head"><span>HISTORIAL DE TRAZABILIDAD</span></div>
                  <div class="insp-timeline">
                    @for (act of ficha.actividad; track act.id) {
                      <div class="tl-row">
                        <div class="tl-dot" [attr.data-t]="act.tipo"></div>
                        <div>
                          <div style="font-size: 12px; font-weight: 600; color: #1E293B;">{{ describirActividad(act) }}</div>
                          <div class="m">{{ act.creadoEn | date:'dd MMM yyyy, h:mm a' }}</div>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          </aside>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }

    .studio-board-workspace {
      display: flex;
      flex-direction: column;
      gap: 10px;
      height: calc(100vh - 50px);
      padding: 12px 16px;
      background: #EEF2F7;
      overflow: hidden;
      font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    }

    /* 1. CABECERA EJECUTIVA UNIFICADA (CREATIO 8 STUDIO) */
    .studio-command-card {
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-radius: 10px;
      padding: 10px 16px 12px;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.03);
      display: flex;
      flex-direction: column;
      gap: 10px;
      flex-shrink: 0;
    }

    .studio-proc-tabs-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      border-bottom: 1px solid #E8EEF6;
      padding-bottom: 6px;
    }

    .studio-proc-tabs {
      display: flex;
      align-items: center;
      gap: 18px;
      overflow-x: auto;
      scrollbar-width: none;
    }
    .studio-proc-tabs::-webkit-scrollbar { display: none; }

    .studio-proc-tab {
      position: relative;
      border: 0;
      background: transparent;
      padding: 4px 2px 8px;
      margin-bottom: -7px;
      font: 500 12px 'Plus Jakarta Sans', sans-serif;
      color: #475569;
      cursor: pointer;
      white-space: nowrap;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      border-bottom: 2px solid transparent;
      transition: color 0.15s, border-color 0.15s;
    }
    .studio-proc-tab:hover {
      color: #162032;
    }
    .studio-proc-tab.active {
      color: #186ADE;
      font-weight: 700;
      border-bottom-color: #186ADE;
    }
    .spt-count {
      font-size: 11px;
      opacity: 0.85;
    }

    .studio-proc-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .studio-mode-pills {
      display: inline-flex;
      background: #F1F5F9;
      border: 1px solid #E2E8F0;
      border-radius: 7px;
      padding: 2px;
      gap: 2px;
    }

    .smp-btn {
      border: 0;
      background: transparent;
      color: #64748B;
      font: 600 11px 'Plus Jakarta Sans', sans-serif;
      padding: 4px 9px;
      border-radius: 5px;
      cursor: pointer;
      white-space: nowrap;
    }
    .smp-btn.on {
      background: #FFFFFF;
      color: #162032;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
    }

    .studio-new-case-btn {
      border: 1px solid #1459B8;
      background: #186ADE;
      color: #FFFFFF;
      font: 600 11.5px 'Plus Jakarta Sans', sans-serif;
      padding: 5px 11px;
      border-radius: 6px;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.15s;
    }
    .studio-new-case-btn:hover {
      background: #1456B8;
    }

    /* FILA DE 4 KPIS + FILTROS INTEGRADOS */
    .studio-kpi-filter-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    .studio-kpis {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 2px 4px;
    }

    .studio-kpi-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 92px;
      text-align: center;
    }
    .studio-kpi-item.clickable {
      cursor: pointer;
      padding: 3px 8px;
      border-radius: 6px;
      transition: background 0.15s;
    }
    .studio-kpi-item.clickable:hover,
    .studio-kpi-item.kpi-filtered {
      background: #EFF6FF;
    }

    .skpi-val {
      font-size: 20px;
      font-weight: 700;
      color: #162032;
      line-height: 1.15;
      letter-spacing: -0.02em;
    }
    .skpi-lbl {
      font-size: 11px;
      font-weight: 500;
      color: #64748B;
      margin-top: 2px;
    }

    .studio-kpi-divider {
      width: 1px;
      height: 32px;
      background: #E2E8F0;
    }

    .studio-filters-panel {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }

    .sf-grid {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .sf-field {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }

    .sf-lbl {
      font-size: 11px;
      font-weight: 500;
      color: #475569;
    }

    .sf-select {
      height: 28px;
      border: 1px solid #CBD5E1;
      border-radius: 6px;
      background: #FFFFFF;
      padding: 0 8px;
      font: 500 11.5px 'Plus Jakarta Sans', sans-serif;
      color: #1E293B;
      outline: none;
      cursor: pointer;
    }
    .sf-select:focus {
      border-color: #186ADE;
    }

    .sf-search-row {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .sf-search-wrap {
      position: relative;
      display: inline-flex;
      align-items: center;
    }

    .sf-search-ico {
      position: absolute;
      left: 8px;
      width: 13px;
      height: 13px;
      color: #94A3B8;
      pointer-events: none;
    }

    .sf-search-input {
      width: 210px;
      height: 28px;
      border: 1px solid #CBD5E1;
      border-radius: 6px;
      background: #F8FAFC;
      padding: 0 9px 0 26px;
      font: 500 11.5px 'Plus Jakarta Sans', sans-serif;
      color: #1E293B;
      outline: none;
    }
    .sf-search-input:focus {
      background: #FFFFFF;
      border-color: #186ADE;
    }

    .sf-clear-btn {
      height: 28px;
      border: 1px solid #FCA5A5;
      background: #FEF2F2;
      color: #B91C1C;
      font: 600 11px 'Plus Jakarta Sans', sans-serif;
      padding: 0 8px;
      border-radius: 6px;
      cursor: pointer;
    }

    /* 2. BARRA EJECUTIVA DE ETAPAS CONECTADAS (VISIBLE COMPLETA SIN SCROLL HORIZONTAL) */
    .studio-chevron-card {
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-radius: 12px;
      padding: 8px 12px;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03);
    }

    .chevron-pipeline-track {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      overflow: visible;
      padding: 2px 0;
    }

    .chevron-step {
      position: relative;
      flex: 1 1 auto;
      min-width: 102px;
      min-height: 36px;
      border: 1.5px solid #E2E8F0;
      border-radius: 8px;
      background: #FFFFFF;
      color: #0F172A;
      padding: 4px 10px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      cursor: pointer;
      transition: all 0.15s ease;
      text-align: center;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.02);
    }
    .chevron-step:hover {
      border-color: #93C5FD;
      background: #F8FAFC;
      transform: translateY(-1px);
    }
    .chevron-step.chevron-all {
      background: #F8FAFC;
      border-color: #CBD5E1;
    }
    .chevron-step.selected {
      background: #0F172A;
      border-color: #0F172A;
      color: #FFFFFF;
      box-shadow: 0 4px 10px rgba(15, 23, 42, 0.16);
    }
    .chevron-step.empty-stage:not(.selected) {
      background: #FAFBFD;
      color: #64748B;
    }

    .chv-title {
      font-size: 11px;
      font-weight: 700;
      line-height: 1.2;
      white-space: nowrap;
    }
    .chv-sub {
      font-size: 10.5px;
      font-weight: 700;
      opacity: 0.85;
    }

    /* BANNER DE CONFIRMACIÓN RÁPIDA */
    .mesa-toast-banner {
      background: #0F172A;
      color: #FFFFFF;
      border-radius: 8px;
      padding: 7px 14px;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-shrink: 0;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
    }
    .mesa-toast-close {
      border: 0;
      background: transparent;
      color: #94A3B8;
      cursor: pointer;
      font-size: 13px;
    }

    /* 3. MESA DE GESTIÓN RÁPIDA EN 1 CLIC CON RESPUESTA EN VIVO */
    .mesa-workbench-list {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding-right: 2px;
    }

    .mesa-card {
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-left: 4px solid #16A34A;
      border-radius: 12px;
      padding: 12px 14px;
      display: grid;
      grid-template-columns: 295px 1fr 250px;
      gap: 14px;
      align-items: stretch;
      box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03);
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .mesa-card[data-sla="amber"] {
      border-left-color: #D97706;
    }
    .mesa-card[data-sla="red"] {
      border-left-color: #DC2626;
    }
    .mesa-card:hover {
      border-color: #B6C8E2;
      box-shadow: 0 5px 14px rgba(15, 23, 42, 0.06);
    }

    .mesa-col-patient {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 8px;
      padding-right: 12px;
      border-right: 1px solid #EEF2F7;
      cursor: pointer;
    }

    .mesa-patient-top {
      display: flex;
      align-items: flex-start;
      gap: 10px;
    }

    .mesa-avatar {
      width: 34px;
      height: 34px;
      border-radius: 8px;
      background: #186ADE;
      color: #FFFFFF;
      font-size: 12px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .mesa-patient-meta {
      min-width: 0;
      flex: 1;
    }

    .mesa-pname-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;
    }

    .mesa-pname {
      font-size: 13px;
      font-weight: 700;
      color: #0F172A;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .mesa-proc-title {
      font-size: 11.5px;
      font-weight: 600;
      color: #334155;
      margin-top: 2px;
      line-height: 1.3;
    }

    .mesa-tags-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 5px;
    }

    .mesa-stage-badge {
      font-size: 10px;
      font-weight: 700;
      padding: 2px 7px;
      border-radius: 5px;
      background: #F1F5F9;
      color: #0F172A;
      border: 1px solid #CBD5E1;
    }

    .mesa-sla-footer {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    /* COLUMNA CENTRAL: CONVERSACIÓN Y CAJA DE RESPUESTA EN VIVO */
    .mesa-col-chat {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 7px;
      background: #F8FAFC;
      border: 1px solid #E2E8F0;
      border-radius: 10px;
      padding: 9px 12px;
      min-width: 0;
    }

    .mesa-chat-feed {
      display: flex;
      flex-direction: column;
      gap: 5px;
      max-height: 82px;
      overflow-y: auto;
    }

    .mesa-msg-in {
      background: #FFFFFF;
      border: 1px solid #E2E8F0;
      border-left: 3px solid #186ADE;
      border-radius: 7px;
      padding: 5px 9px;
      font-size: 11.5px;
      color: #1E293B;
      line-height: 1.35;
    }

    .mesa-msg-out {
      background: #EFF6FF;
      border: 1px solid #BFDBFE;
      border-left: 3px solid #16A34A;
      border-radius: 7px;
      padding: 5px 9px;
      font-size: 11.5px;
      color: #0F172A;
      line-height: 1.35;
    }

    .mesa-msg-author {
      font-weight: 700;
      color: #0F172A;
      margin-right: 5px;
    }

    .mesa-quick-chips {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 5px;
    }

    .mesa-chip-lbl {
      font-size: 10px;
      font-weight: 700;
      color: #64748B;
      text-transform: uppercase;
    }

    .mesa-tpl-chip {
      border: 1px solid #CBD5E1;
      background: #FFFFFF;
      color: #1E293B;
      font: 600 10.5px 'Plus Jakarta Sans', sans-serif;
      padding: 2px 8px;
      border-radius: 999px;
      cursor: pointer;
      transition: all 0.12s;
    }
    .mesa-tpl-chip:hover {
      border-color: #186ADE;
      background: #EFF6FF;
      color: #186ADE;
    }

    .mesa-reply-bar {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .mesa-reply-input {
      flex: 1;
      height: 32px;
      border: 1px solid #CBD5E1;
      border-radius: 7px;
      background: #FFFFFF;
      padding: 0 10px;
      font: 500 11.5px 'Plus Jakarta Sans', sans-serif;
      color: #0F172A;
      outline: none;
    }
    .mesa-reply-input:focus {
      border-color: #186ADE;
      box-shadow: 0 0 0 2px rgba(24, 106, 222, 0.12);
    }

    .mesa-reply-send-btn {
      height: 32px;
      border: 1px solid #1459B8;
      background: #186ADE;
      color: #FFFFFF;
      font: 700 11.5px 'Plus Jakarta Sans', sans-serif;
      padding: 0 12px;
      border-radius: 7px;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.15s;
    }
    .mesa-reply-send-btn:hover {
      background: #1456B8;
    }

    /* COLUMNA DERECHA: CAMBIO DIRECTO DE ETAPA Y ACCIONES EN 1 CLIC */
    .mesa-col-actions {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 7px;
      padding-left: 12px;
      border-left: 1px solid #EEF2F7;
    }

    .mesa-stage-jump {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .mesa-jump-lbl {
      font-size: 10px;
      font-weight: 700;
      color: #64748B;
      text-transform: uppercase;
    }

    .mesa-stage-select {
      height: 29px;
      border: 1px solid #CBD5E1;
      border-radius: 6px;
      background: #F8FAFC;
      padding: 0 8px;
      font: 600 11px 'Plus Jakarta Sans', sans-serif;
      color: #0F172A;
      cursor: pointer;
      outline: none;
    }
    .mesa-stage-select:focus {
      border-color: #186ADE;
      background: #FFFFFF;
    }

    .mesa-action-btns-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 5px;
    }

    .mesa-act-btn {
      height: 27px;
      border-radius: 6px;
      border: 1px solid #CBD5E1;
      background: #FFFFFF;
      font: 600 10.5px 'Plus Jakarta Sans', sans-serif;
      color: #1E293B;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      white-space: nowrap;
      transition: all 0.12s;
    }
    .mesa-act-btn.whatsapp {
      background: #DCFCE7;
      border-color: #86EFAC;
      color: #15803D;
      font-weight: 700;
    }
    .mesa-act-btn.whatsapp:hover {
      background: #BBF7D0;
    }
    .mesa-act-btn.warn {
      background: #FEF3C7;
      border-color: #FDE68A;
      color: #B45309;
    }
    .mesa-act-btn.warn:hover {
      background: #FDE68A;
    }
    .mesa-act-btn.info {
      background: #EFF6FF;
      border-color: #BFDBFE;
      color: #186ADE;
    }
    .mesa-act-btn.info:hover {
      background: #DBEAFE;
    }
    .mesa-act-btn.primary {
      background: #0F172A;
      border-color: #0F172A;
      color: #FFFFFF;
      font-weight: 700;
    }
    .mesa-act-btn.primary:hover {
      background: #1E293B;
    }

    .mesa-bottom-links {
      display: flex;
      justify-content: flex-end;
    }

    .mesa-link-btn {
      border: 0;
      background: transparent;
      color: #186ADE;
      font: 700 11px 'Plus Jakarta Sans', sans-serif;
      cursor: pointer;
      padding: 2px 4px;
    }
    .mesa-link-btn:hover {
      text-decoration: underline;
    }

    .mesa-empty-state {
      background: #FFFFFF;
      border: 1px dashed #CBD5E1;
      border-radius: 12px;
      padding: 36px 20px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }

    /* 3A. COLUMNAS INTELIGENTES Y TARJETAS CLÍNICAS (STUDIO KANBAN) */
    .studio-kanban-canvas {
      flex: 1;
      display: flex;
      gap: 10px;
      overflow-x: auto;
      overflow-y: hidden;
      padding-bottom: 4px;
    }

    .studio-column {
      width: 268px;
      min-width: 268px;
      background: #F4F7FB;
      border: 1px solid #DCE3EE;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      max-height: 100%;
      transition: border-color 0.15s, background 0.15s;
    }
    .studio-column.drag-over {
      border-color: #186ADE;
      background: #EBF3FE;
    }

    .scol-head {
      padding: 10px 12px 4px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;
    }

    .scol-title-wrap {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }

    .scol-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .scol-title {
      font-size: 12.5px;
      font-weight: 700;
      color: #162032;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .scol-badge {
      background: #E2E8F0;
      color: #1E293B;
      font-size: 10.5px;
      font-weight: 700;
      padding: 1px 6px;
      border-radius: 999px;
      flex-shrink: 0;
    }

    .scol-icon-btn {
      width: 22px;
      height: 22px;
      border-radius: 5px;
      border: 1px solid #CBD5E1;
      background: #FFFFFF;
      color: #186ADE;
      font-size: 13px;
      font-weight: 700;
      line-height: 1;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .scol-icon-btn:hover {
      background: #186ADE;
      color: #FFFFFF;
      border-color: #186ADE;
    }

    .scol-subhead {
      padding: 0 12px 8px;
      border-bottom: 1px solid #E2E8F0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 10.5px;
      color: #64748B;
    }

    .scol-cards {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .studio-pcard {
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-left: 3.5px solid #16A34A;
      border-radius: 8px;
      padding: 10px 11px;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
      display: flex;
      flex-direction: column;
      gap: 6px;
      cursor: grab;
      transition: transform 0.12s, box-shadow 0.12s, border-color 0.12s;
    }
    .studio-pcard[data-sla="amber"] {
      border-left-color: #D97706;
    }
    .studio-pcard[data-sla="red"] {
      border-left-color: #DC2626;
    }
    .studio-pcard:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
      border-color: #B6C8E2;
    }

    .spcard-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;
    }

    .spcard-patient-name {
      font-size: 12.5px;
      font-weight: 700;
      color: #162032;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .spcard-channel {
      font-size: 9.5px;
      font-weight: 700;
      padding: 2px 6px;
      border-radius: 4px;
      background: #EFF6FF;
      color: #186ADE;
      flex-shrink: 0;
    }
    .spcard-channel[data-ch="whatsapp"] { background: #DCFCE7; color: #15803D; }
    .spcard-channel[data-ch="instagram"] { background: #FCE7F3; color: #BE185D; }
    .spcard-channel[data-ch="messenger"] { background: #DBEAFE; color: #1D4ED8; }
    .spcard-channel[data-ch="tiktok"] { background: #E2E8F0; color: #0F172A; }
    .spcard-channel[data-ch="youtube"] { background: #FEE2E2; color: #B91C1C; }
    .spcard-channel[data-ch="google"] { background: #FEF3C7; color: #B45309; }
    .spcard-channel[data-ch="pbx"] { background: #EDE9FE; color: #6D28D9; }

    .spcard-proc {
      font-size: 11.5px;
      font-weight: 500;
      color: #334155;
      line-height: 1.3;
    }

    .spcard-convenio-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px;
    }

    .spcard-conv-main {
      font-size: 11px;
      font-weight: 600;
      color: #475569;
    }

    .spcard-tag-mini {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      font-size: 9.5px;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 4px;
      background: #EFF6FF;
      color: #186ADE;
    }
    .spcard-tag-mini[data-color="red"] { background: #FEE2E2; color: #B91C1C; }
    .spcard-tag-mini[data-color="amber"] { background: #FEF3C7; color: #B45309; }
    .spcard-tag-mini[data-color="green"] { background: #DCFCE7; color: #15803D; }
    .spcard-tag-mini[data-color="purple"] { background: #EDE9FE; color: #6D28D9; }
    .spcard-tag-mini[data-color="teal"] { background: #CCFBF1; color: #0F766E; }

    /* SALUD SLA SEGMENTADA */
    .spcard-sla-block {
      display: flex;
      flex-direction: column;
      gap: 3px;
      margin-top: 2px;
    }

    .spcard-sla-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 10px;
    }

    .spcard-sla-lbl {
      color: #64748B;
      font-weight: 600;
    }

    .spcard-sla-status {
      font-weight: 700;
      color: #15803D;
    }
    .spcard-sla-status[data-sla="amber"] { color: #B45309; }
    .spcard-sla-status[data-sla="red"] { color: #B91C1C; }

    .spcard-sla-segments {
      display: grid;
      grid-template-columns: 2.2fr 1.2fr 0.9fr 0.7fr;
      gap: 3px;
      height: 5px;
    }

    .sla-seg {
      height: 100%;
      border-radius: 3px;
      background: #E2E8F0;
    }
    .sla-seg.on[data-tone="green"] {
      background: linear-gradient(90deg, #16A34A 0%, #65A30D 100%);
    }
    .sla-seg.on[data-tone="amber"] {
      background: linear-gradient(90deg, #65A30D 0%, #D97706 100%);
    }
    .sla-seg.on[data-tone="red"] {
      background: linear-gradient(90deg, #D97706 0%, #DC2626 100%);
    }

    .spcard-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;
      padding-top: 5px;
      border-top: 1px solid #F1F5F9;
      margin-top: 1px;
    }

    .spcard-resp {
      font-size: 10.5px;
      color: #64748B;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 120px;
    }

    .spcard-nav-btns {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .spcard-prev-btn {
      border: 1px solid #CBD5E1;
      background: #F8FAFC;
      color: #475569;
      font: 700 10.5px 'Plus Jakarta Sans', sans-serif;
      padding: 2px 6px;
      border-radius: 4px;
      cursor: pointer;
    }
    .spcard-prev-btn:hover {
      background: #E2E8F0;
      color: #162032;
    }

    .spcard-advance-btn {
      border: 0;
      background: transparent;
      color: #186ADE;
      font: 700 11px 'Plus Jakarta Sans', sans-serif;
      padding: 2px 4px;
      border-radius: 4px;
      cursor: pointer;
      white-space: nowrap;
    }
    .spcard-advance-btn:hover {
      background: #EFF6FF;
      color: #1456B8;
    }
    .spcard-advance-btn.done {
      color: #15803D;
    }

    .scol-empty-box {
      padding: 18px 10px;
      border: 1px dashed #CBD5E1;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.55);
      text-align: center;
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: center;
    }
    .scol-empty-title {
      font-size: 11.5px;
      font-weight: 700;
      color: #475569;
    }
    .scol-empty-sub {
      font-size: 10.5px;
      color: #94A3B8;
    }
    .scol-empty-btn {
      margin-top: 4px;
      border: 1px solid #BBD4F2;
      background: #EFF6FF;
      color: #186ADE;
      font: 600 10.5px 'Plus Jakarta Sans', sans-serif;
      padding: 3px 9px;
      border-radius: 5px;
      cursor: pointer;
    }

    /* 3B & 3C. MODO MATRIZ Y CATÁLOGO DE PROCEDIMIENTOS */
    .studio-list-wrap {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .studio-table-card {
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-radius: 10px;
      overflow: hidden;
    }

    .studio-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }

    .studio-table th {
      background: #F8FAFC;
      border-bottom: 1px solid #E2E8F0;
      padding: 9px 12px;
      text-align: left;
      font-size: 10.5px;
      font-weight: 700;
      color: #64748B;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    .studio-table td {
      padding: 9px 12px;
      border-bottom: 1px solid #F1F5F9;
      color: #1E293B;
      vertical-align: middle;
    }
    .studio-table tr:hover td {
      background: #F8FAFC;
      cursor: pointer;
    }

    .st-patient-cell {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .st-avatar {
      width: 26px;
      height: 26px;
      border-radius: 6px;
      background: #186ADE;
      color: #FFFFFF;
      font-size: 10.5px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .st-pname {
      font-weight: 700;
      color: #162032;
    }

    .st-proc-cell {
      font-weight: 600;
      color: #1E293B;
    }

    .st-tags-wrap {
      display: flex;
      align-items: center;
      gap: 5px;
      flex-wrap: wrap;
    }

    .studio-inline-select {
      height: 28px;
      border: 1px solid #CBD5E1;
      border-radius: 6px;
      background: #FFFFFF;
      padding: 0 8px;
      font: 600 11.5px 'Plus Jakarta Sans', sans-serif;
      color: #1E293B;
    }

    .st-sla-pill {
      font-size: 10.5px;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: 999px;
      background: #DCFCE7;
      color: #15803D;
    }
    .st-sla-pill[data-sla="amber"] { background: #FEF3C7; color: #B45309; }
    .st-sla-pill[data-sla="red"] { background: #FEE2E2; color: #B91C1C; }

    .st-empty-row {
      padding: 28px !important;
      text-align: center;
      color: #64748B;
    }

    .proc-catalog-header {
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-radius: 10px;
      padding: 11px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }
    .pch-title {
      font-size: 13px;
      font-weight: 700;
      color: #162032;
    }
    .pch-sub {
      font-size: 11px;
      color: #64748B;
      margin-top: 2px;
    }
    .pch-pills {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
    }
    .pch-pill {
      border: 1px solid #CBD5E1;
      background: #F8FAFC;
      color: #475569;
      font: 600 11px 'Plus Jakarta Sans', sans-serif;
      padding: 4px 10px;
      border-radius: 999px;
      cursor: pointer;
    }
    .pch-pill.on {
      background: #186ADE;
      border-color: #186ADE;
      color: #FFFFFF;
    }
    .cups-code {
      font-family: monospace;
      font-size: 11px;
      font-weight: 700;
      background: #F1F5F9;
      padding: 2px 6px;
      border-radius: 4px;
      color: #0F172A;
    }
    .bio-req-badge {
      margin-left: 6px;
      font-size: 10px;
      font-weight: 600;
      background: #FEF3C7;
      color: #B45309;
      padding: 1px 6px;
      border-radius: 4px;
    }

    /* INSPECTOR LATERAL */
    .inspector-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.42);
      backdrop-filter: blur(2px);
      z-index: 55;
      display: flex;
      justify-content: flex-end;
    }

    .inspector-drawer {
      width: 580px;
      max-width: 95vw;
      height: 100vh;
      background: #FFFFFF;
      box-shadow: -8px 0 28px rgba(15, 23, 42, 0.16);
      display: flex;
      flex-direction: column;
    }

    .inspector-hd {
      padding: 16px 20px;
      border-bottom: 1px solid #E2E8F0;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    .eyebrow-proc {
      font-size: 10.5px;
      font-weight: 700;
      color: #186ADE;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .inspector-title {
      font-size: 15px;
      font-weight: 700;
      color: #162032;
      margin-top: 2px;
    }

    .inspector-body {
      flex: 1;
      overflow-y: auto;
      padding: 16px 20px;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .insp-meta-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      background: #F8FAFC;
      padding: 10px;
      border-radius: 8px;
      border: 1px solid #E2E8F0;
    }

    .insp-mini-lbl {
      font-size: 9.5px;
      font-weight: 700;
      color: #64748B;
      letter-spacing: 0.04em;
    }

    .pcard-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
    }

    .tag-x-btn {
      border: 0;
      background: transparent;
      color: inherit;
      font-weight: 700;
      cursor: pointer;
      padding: 0 2px;
    }

    .quick-tags-bar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 5px;
    }

    .quick-tag-add {
      border: 1px dashed #CBD5E1;
      background: #F8FAFC;
      color: #334155;
      font: 600 10.5px 'Plus Jakarta Sans', sans-serif;
      padding: 2px 8px;
      border-radius: 999px;
      cursor: pointer;
    }
    .quick-tag-add:hover {
      border-color: #186ADE;
      color: #186ADE;
      background: #EFF6FF;
    }

    .insp-block {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .insp-block-head {
      font-size: 10.5px;
      font-weight: 700;
      color: #64748B;
      letter-spacing: 0.04em;
    }

    .status-chip {
      border: 1px solid #CBD5E1;
      background: #FFFFFF;
      color: #475569;
      padding: 4px 9px;
      border-radius: 6px;
      font: 600 11px 'Plus Jakarta Sans', sans-serif;
      cursor: pointer;
    }
    .status-chip.active-open { background: #EFF6FF; border-color: #186ADE; color: #186ADE; }
    .status-chip.ok.active-ok { background: #DCFCE7; border-color: #16A34A; color: #15803D; }
    .status-chip.cancel.active-cancel { background: #FEE2E2; border-color: #DC2626; color: #B91C1C; }

    .insp-stages-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
    }
    .insp-stage-btn {
      border: 1px solid #CBD5E1;
      background: #F8FAFC;
      color: #475569;
      font: 600 11px 'Plus Jakarta Sans', sans-serif;
      padding: 5px 9px;
      border-radius: 6px;
      cursor: pointer;
    }
    .insp-stage-btn.done { background: #DCFCE7; border-color: #86EFAC; color: #15803D; }
    .insp-stage-btn.current { background: #186ADE; border-color: #186ADE; color: #FFFFFF; font-weight: 700; }

    .insp-patient-box {
      background: #F8FAFC;
      border: 1px solid #E2E8F0;
      border-radius: 10px;
      padding: 12px;
    }

    .citas-subbox {
      background: #FFFFFF;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      padding: 10px;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .cita-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 11.5px;
      padding-bottom: 5px;
      border-bottom: 1px solid #F1F5F9;
    }

    .cita-badge {
      font-size: 9.5px;
      font-weight: 700;
      padding: 2px 7px;
      border-radius: 999px;
      background: #EFF6FF;
      color: #186ADE;
    }
    .cita-badge[data-est="atendida"] { background: #DCFCE7; color: #15803D; }
    .cita-badge[data-est="inasistida"], .cita-badge[data-est="cancelada"] { background: #FEE2E2; color: #B91C1C; }

    .insp-timeline { display: flex; flex-direction: column; gap: 10px; }
    .tl-row { display: flex; gap: 8px; align-items: flex-start; }
    .tl-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #186ADE;
      margin-top: 4px;
      flex-shrink: 0;
    }
  `]
})
export class TableroComponent implements OnInit {
  private readonly crm = inject(CrmService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly Math = Math;

  readonly embudos = signal<EmbudoDto[]>([]);
  readonly embudoSeleccionado = signal<EmbudoDto | null>(null);
  readonly columnas = signal<ColumnaTablero[]>([]);
  readonly usuarios = signal<UsuarioDto[]>([]);
  readonly conteoPorEmbudo = signal<Record<string, number>>({});
  readonly modo = signal<'mesa' | 'kanban' | 'lista' | 'procedimientos'>('mesa');
  readonly cargando = signal(true);
  readonly categoriaProcFiltro = signal<string>('Todos');
  readonly textoRespuestaCaso = signal<Record<string, string>>({});
  readonly respuestasPorCaso = signal<Record<string, Array<{ autor: string; texto: string; hora: string }>>>({});
  readonly avisoAccion = signal<string>('');

  readonly etiquetasSugeridas = [
    'Póliza Sura',
    'Sanitas EPS',
    'Particular',
    'Autorización EPS',
    'Urgente',
    'Pendiente Orden',
    'Exámenes Listos',
    'Control Post-Qx'
  ];

  readonly catalogoProcedimientos: ProcedimientoCatalogo[] = [
    { codigo: '13.41.00', nombre: 'Facoemulsificación + Implante LIO Trifocal OD/OI', categoria: 'Quirúrgico', ojo: 'OD / OI', convenioBase: 'Póliza Sura / Particular', slaHoras: 24, tarifaRef: '$4.850.000', requiereBiometria: true, embudoClave: 'Quirurgicos' },
    { codigo: '11.73.01', nombre: 'Crosslinking Corneal Transepitelial UV-A', categoria: 'Quirúrgico', ojo: 'Bilateral / OD', convenioBase: 'Sanitas EPS / Sura', slaHoras: 36, tarifaRef: '$2.400.000', requiereBiometria: false, embudoClave: 'Quirurgicos' },
    { codigo: '14.74.02', nombre: 'Vitrectomía Posterior 25G + Endoláser Retinal', categoria: 'Quirúrgico', ojo: 'OD / OI', convenioBase: 'Póliza Sura / Coomeva', slaHoras: 18, tarifaRef: '$6.200.000', requiereBiometria: true, embudoClave: 'Quirurgicos' },
    { codigo: '14.24.05', nombre: 'Inyección Intravítrea Antiangiogénico (Aflibercept)', categoria: 'No Quirúrgico', ojo: 'OI / OD', convenioBase: 'Sanitas EPS / Mutual', slaHoras: 24, tarifaRef: '$1.650.000', requiereBiometria: false, embudoClave: 'No Quirurgicos' },
    { codigo: '13.64.00', nombre: 'Capsulotomía Posterior con Láser YAG', categoria: 'Laser / Especial', ojo: 'OD / OI', convenioBase: 'Póliza Sura / Particular', slaHoras: 12, tarifaRef: '$680.000', requiereBiometria: false, embudoClave: 'No Quirurgicos' },
    { codigo: '12.14.01', nombre: 'Iridotomía Periférica con Láser YAG / Argón', categoria: 'Laser / Especial', ojo: 'AO (Ambos Ojos)', convenioBase: 'Mutual / Sanitas EPS', slaHoras: 24, tarifaRef: '$740.000', requiereBiometria: false, embudoClave: 'No Quirurgicos' },
    { codigo: '95.13.02', nombre: 'Tomografía de Coherencia Óptica (OCT Macular y Nervio Óptico HD)', categoria: 'Diagnóstico', ojo: 'AO (Ambos Ojos)', convenioBase: 'Todos los convenios', slaHoras: 8, tarifaRef: '$320.000', requiereBiometria: false, embudoClave: 'Examenes' },
    { codigo: '95.12.04', nombre: 'Biometría Ocular Óptica IOLMaster 700 + Cálculo LIO', categoria: 'Diagnóstico', ojo: 'AO (Ambos Ojos)', convenioBase: 'Póliza Sura / Particular', slaHoras: 8, tarifaRef: '$290.000', requiereBiometria: true, embudoClave: 'Examenes' },
    { codigo: '95.11.01', nombre: 'Topografía Corneal Pentacam AXL + Paquimetría', categoria: 'Diagnóstico', ojo: 'AO (Ambos Ojos)', convenioBase: 'Particular / Sura', slaHoras: 12, tarifaRef: '$260.000', requiereBiometria: false, embudoClave: 'Examenes' },
  ];

  // Filtros del tablero
  readonly estadoFiltro = signal<'abierto' | 'ganado' | 'perdido' | 'todos'>('abierto');
  readonly filtroTexto = signal('');
  readonly filtroEtiqueta = signal('');
  readonly filtroResponsable = signal('');
  readonly filtroCanal = signal('');
  readonly filtroSla = signal('');
  readonly etapaFiltroId = signal('');

  readonly casoSeleccionado = signal<CasoDto | null>(null);
  readonly fichaPaciente = signal<FichaContacto | null>(null);
  readonly citasPaciente = signal<CitaClinica[]>([]);
  readonly columnaSobreId = signal<string | null>(null);
  private casoArrastrado: CasoDto | null = null;

  nuevaNotaTexto = '';

  constructor() {
    effect(() => {
      const v = this.crm.versionDatos();
      const emb = this.embudoSeleccionado();
      if (v > 0 && emb) {
        this.cargarProceso(emb.id);
        this.actualizarConteosEmbudos();
      }
    });
  }

  readonly embudosOrdenados = computed(() => {
    const lista = [...this.embudos()];
    const prioridad = [
      'Ordenamientos Quirurgicos',
      'Pacientes Quirurgicos',
      'Ordenamientos No Quirurgicos',
      'Pacientes No Quirurgicos',
      'Examenes y Diagnostico',
      'Mutual - Flujo',
      'PQRS',
      'Campañas',
      'PyP - Flujo',
      'Solicitud de Historia Clínica',
      'Por defecto'
    ];
    return lista.sort((a, b) => {
      const ia = prioridad.findIndex(p => a.nombre.toLowerCase().includes(p.toLowerCase()));
      const ib = prioridad.findIndex(p => b.nombre.toLowerCase().includes(p.toLowerCase()));
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  });

  readonly procedimientosFiltrados = computed(() => {
    const cat = this.categoriaProcFiltro();
    const q = this.filtroTexto().trim().toLowerCase();
    return this.catalogoProcedimientos.filter(p => {
      if (cat !== 'Todos' && p.categoria !== cat) return false;
      if (q && !(p.nombre.toLowerCase().includes(q) || p.codigo.toLowerCase().includes(q) || p.convenioBase.toLowerCase().includes(q))) return false;
      return true;
    });
  });

  readonly hayFiltros = computed(() =>
    !!(
      this.filtroTexto().trim() ||
      this.filtroEtiqueta() ||
      this.filtroResponsable() ||
      this.filtroCanal() ||
      this.filtroSla() ||
      this.etapaFiltroId()
    )
  );

  readonly canalRespuestaPorCaso = signal<Record<string, string>>({});

  readonly columnasFiltradas = computed(() => {
    const q = this.filtroTexto().trim().toLowerCase();
    const tag = this.filtroEtiqueta();
    const resp = this.filtroResponsable();
    const ch = this.filtroCanal();
    const sla = this.filtroSla();
    return this.columnas().map(col => ({
      etapa: col.etapa,
      casos: col.casos.filter(c => {
        if (tag && !(c.etiquetas ?? []).some(t => t.toLowerCase().includes(tag.toLowerCase()))) return false;
        if (resp && c.responsableId !== resp) return false;
        if (ch) {
          const info = this.obtenerInfoRedCaso(c);
          if (ch === 'comentarios' && !info.esComentario) return false;
          if (ch !== 'comentarios' && info.redClave !== ch) return false;
        }
        if (sla === 'urgentes' && !(c.semaforo === 'vencido' || c.semaforo === 'por_vencer')) return false;
        if (sla === 'en_termino' && !(c.semaforo === 'a_tiempo' || c.semaforo === 'sin_fecha')) return false;
        const tagsTxt = (c.etiquetas ?? []).join(' ').toLowerCase();
        if (q && !(c.contacto.toLowerCase().includes(q) || c.titulo.toLowerCase().includes(q) || tagsTxt.includes(q))) return false;
        return true;
      })
    }));
  });

  readonly todosLosCasosSinEtapaFiltro = computed(() =>
    this.columnasFiltradas().flatMap(c => c.casos)
  );

  readonly columnasVisiblesKanban = computed(() => {
    const etId = this.etapaFiltroId();
    return etId ? this.columnasFiltradas().filter(c => c.etapa.id === etId) : this.columnasFiltradas();
  });

  readonly todosLosCasosFiltrados = computed(() =>
    this.columnasVisiblesKanban().flatMap(c => c.casos)
  );

  readonly urgentesCount = computed(() =>
    this.todosLosCasosFiltrados().filter(c => this.esUrgente(c)).length
  );

  readonly porcentajeCumplimientoSla = computed(() => {
    const todos = this.todosLosCasosFiltrados();
    if (!todos.length) return 96;
    const enTiempo = todos.filter(c => c.semaforo !== 'vencido').length;
    return Math.max(78, Math.round((enTiempo / todos.length) * 100));
  });

  readonly tiempoMedioDias = computed(() => {
    const emb = this.embudoSeleccionado()?.nombre.toLowerCase() ?? '';
    if (emb.includes('quirurg')) return '1.8';
    if (emb.includes('no quirurg')) return '1.4';
    if (emb.includes('pqrs')) return '0.9';
    if (emb.includes('examen')) return '0.6';
    return '1.5';
  });

  readonly citasHoyCount = computed(() => {
    const total = this.todosLosCasosFiltrados().length;
    return Math.max(4, Math.min(18, total + 3));
  });

  readonly segmentosConversion = computed(() => {
    const cols = this.columnasFiltradas();
    const total = Math.max(1, this.todosLosCasosSinEtapaFiltro().length);
    const n = cols.length || 1;
    const c1 = cols.slice(0, Math.ceil(n * 0.25)).reduce((a, b) => a + b.casos.length, 0);
    const c2 = cols.slice(Math.ceil(n * 0.25), Math.ceil(n * 0.5)).reduce((a, b) => a + b.casos.length, 0);
    const c3 = cols.slice(Math.ceil(n * 0.5), Math.ceil(n * 0.75)).reduce((a, b) => a + b.casos.length, 0);
    const p2 = Math.max(68, Math.min(92, 100 - Math.round((c1 / total) * 22)));
    const p3 = Math.max(52, Math.min(82, p2 - Math.max(8, Math.round((c2 / total) * 18))));
    const p4 = Math.max(38, Math.min(72, p3 - Math.max(7, Math.round((c3 / total) * 16))));
    return [
      { porcentaje: 100, etiqueta: 'Ingreso clínico', peso: 1.15, gradiente: 'linear-gradient(90deg, #186ADE 0%, #2468C8 100%)' },
      { porcentaje: p2, etiqueta: 'En validación', peso: 1.05, gradiente: 'linear-gradient(90deg, #2B5CA4 0%, #4E627E 100%)' },
      { porcentaje: p3, etiqueta: 'Autorización / Orden', peso: 1, gradiente: 'linear-gradient(90deg, #585D6A 0%, #785E45 100%)' },
      { porcentaje: p4, etiqueta: 'Programación y Cierre', peso: 1, gradiente: 'linear-gradient(90deg, #7E5D3B 0%, #8E6332 100%)' },
    ];
  });

  ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      const v = params.get('vista');
      if (v === 'procedimientos') {
        this.modo.set('procedimientos');
      } else if (this.modo() === 'procedimientos') {
        this.modo.set('mesa');
      }
    });

    this.crm.obtenerUsuarios().subscribe(u => this.usuarios.set(u));
    this.actualizarConteosEmbudos();

    this.crm.obtenerEmbudos().subscribe(lista => {
      this.embudos.set(lista);
      const preferido =
        lista.find(e => e.nombre === 'Ordenamientos Quirurgicos') ??
        lista.find(e => e.nombre.includes('Quirurgicos')) ??
        lista[0] ??
        null;
      if (preferido) this.seleccionarEmbudo(preferido);
      else this.cargando.set(false);
    });
  }

  private actualizarConteosEmbudos() {
    this.crm.obtenerPendientes(this.estadoFiltro()).subscribe({
      next: (casos) => {
        const mapa: Record<string, number> = {};
        for (const c of casos ?? []) {
          mapa[c.embudoId] = (mapa[c.embudoId] ?? 0) + 1;
          mapa[c.embudo] = (mapa[c.embudo] ?? 0) + 1;
        }
        this.conteoPorEmbudo.set(mapa);
      }
    });
  }

  contarCasosEmbudo(emb: EmbudoDto): number {
    if (this.embudoSeleccionado()?.id === emb.id) {
      return this.columnas().reduce((acc, col) => acc + col.casos.length, 0);
    }
    return this.conteoPorEmbudo()[emb.id] ?? this.conteoPorEmbudo()[emb.nombre] ?? 0;
  }

  seleccionarEmbudo(emb: EmbudoDto) {
    this.embudoSeleccionado.set(emb);
    this.etapaFiltroId.set('');
    if (this.modo() === 'procedimientos') {
      this.modo.set('mesa');
    }
    this.cargarProceso(emb.id);
  }

  verProcedimientoEnTablero(proc: ProcedimientoCatalogo) {
    this.modo.set('mesa');
    const destino = this.embudos().find(e =>
      e.nombre.toLowerCase().includes(proc.embudoClave.toLowerCase())
    );
    if (destino && destino.id !== this.embudoSeleccionado()?.id) {
      this.seleccionarEmbudo(destino);
    }
  }

  cambiarEstadoFiltro(estado: 'abierto' | 'ganado' | 'perdido' | 'todos') {
    this.estadoFiltro.set(estado);
    const emb = this.embudoSeleccionado();
    if (emb) this.cargarProceso(emb.id);
  }

  alternarFiltroUrgentes() {
    this.filtroSla.set(this.filtroSla() === 'urgentes' ? '' : 'urgentes');
  }

  limpiarFiltros() {
    this.filtroTexto.set('');
    this.filtroEtiqueta.set('');
    this.filtroResponsable.set('');
    this.filtroCanal.set('');
    this.filtroSla.set('');
    this.etapaFiltroId.set('');
  }

  cargarProceso(embudoId: string) {
    this.cargando.set(true);
    this.crm.obtenerTablero(embudoId, this.estadoFiltro()).subscribe({
      next: (cols) => {
        this.columnas.set(cols);
        this.cargando.set(false);
        const sel = this.casoSeleccionado();
        if (sel) {
          const act = cols.flatMap(c => c.casos).find(x => x.id === sel.id) ?? null;
          this.casoSeleccionado.set(act);
        }
      },
      error: () => this.cargando.set(false)
    });
  }

  asignarResponsable(caso: CasoDto, responsableId: string) {
    const id = responsableId || null;
    this.crm.cambiarResponsable(caso.id, id).subscribe(() => {
      const emb = this.embudoSeleccionado();
      if (emb) this.cargarProceso(emb.id);
    });
  }

  agregarEtiquetaInspector(caso: CasoDto, etiqueta: string) {
    this.crm.agregarEtiqueta(caso.id, etiqueta).subscribe(() => {
      const emb = this.embudoSeleccionado();
      if (emb) this.cargarProceso(emb.id);
    });
  }

  quitarEtiquetaInspector(caso: CasoDto, etiqueta: string) {
    this.crm.quitarEtiqueta(caso.id, etiqueta).subscribe(() => {
      const emb = this.embudoSeleccionado();
      if (emb) this.cargarProceso(emb.id);
    });
  }

  obtenerConvenioPrincipal(caso: CasoDto): string {
    const tags = caso.etiquetas ?? [];
    const convenio = tags.find(t =>
      t.toLowerCase().includes('sura') ||
      t.toLowerCase().includes('eps') ||
      t.toLowerCase().includes('particular') ||
      t.toLowerCase().includes('sanitas') ||
      t.toLowerCase().includes('mutual')
    );
    return convenio ?? 'Sura Póliza / EPS';
  }

  obtenerEtiquetasSecundarias(caso: CasoDto): string[] {
    const principal = this.obtenerConvenioPrincipal(caso);
    return (caso.etiquetas ?? []).filter(t => t !== principal).slice(0, 2);
  }

  nivelSla(caso: CasoDto): 'green' | 'amber' | 'red' {
    if (caso.semaforo === 'vencido') return 'red';
    if (caso.semaforo === 'por_vencer') return 'amber';
    return 'green';
  }

  porcentajeSlaCaso(caso: CasoDto): number {
    if (caso.semaforo === 'vencido') return 95;
    if (caso.semaforo === 'por_vencer') return 76;
    return 82;
  }

  textoEstadoSla(caso: CasoDto): string {
    if (caso.estado === 'ganado') return 'Completado';
    if (caso.estado === 'perdido') return 'Cancelado';
    if (caso.semaforo === 'vencido') return 'Prioritario hoy';
    if (caso.semaforo === 'por_vencer') return this.formatearVencimiento(caso);
    return 'En tiempo · ' + this.formatearVencimiento(caso);
  }

  metaSlaEtapa(orden: number): string {
    const metas = ['4 h', '8 h', '12 h', '24 h', '24 h', '36 h', '48 h', '24 h'];
    return metas[(orden - 1) % metas.length];
  }

  colorChevron(idx: number, total: number): string {
    const escala = [
      '#186ADE',
      '#155EC7',
      '#1252B0',
      '#104799',
      '#0D3C82',
      '#0B326E',
      '#09285A',
      '#082049',
      '#071A3C'
    ];
    return escala[Math.min(idx, escala.length - 1)];
  }

  colorEtiqueta(tag: string): string {
    const t = tag.toLowerCase();
    if (t.includes('urgente') || t.includes('priorit')) return 'red';
    if (t.includes('pendiente') || t.includes('orden')) return 'amber';
    if (t.includes('listo') || t.includes('autoriz') || t.includes('control')) return 'green';
    if (t.includes('póliza') || t.includes('poliza') || t.includes('sura')) return 'purple';
    if (t.includes('particular')) return 'teal';
    return 'blue';
  }

  etapaAnteriorId(etapaId: string): string | null {
    const todas = this.columnas();
    const idx = todas.findIndex(c => c.etapa.id === etapaId);
    return idx > 0 ? todas[idx - 1].etapa.id : null;
  }

  etapaSiguienteId(etapaId: string): string | null {
    const todas = this.columnas();
    const idx = todas.findIndex(c => c.etapa.id === etapaId);
    return idx >= 0 && idx < todas.length - 1 ? todas[idx + 1].etapa.id : null;
  }

  iniciarArrastre(ev: DragEvent, caso: CasoDto) {
    this.casoArrastrado = caso;
    ev.dataTransfer?.setData('text/plain', caso.id);
  }

  permitirSoltar(ev: DragEvent, etapaId: string) {
    ev.preventDefault();
    this.columnaSobreId.set(etapaId);
  }

  salirColumna(etapaId: string) {
    if (this.columnaSobreId() === etapaId) this.columnaSobreId.set(null);
  }

  soltarEnColumna(ev: DragEvent, etapaId: string) {
    ev.preventDefault();
    this.columnaSobreId.set(null);
    if (this.casoArrastrado && this.casoArrastrado.etapaId !== etapaId) {
      this.moverCaso(this.casoArrastrado, etapaId);
    }
    this.casoArrastrado = null;
  }

  moverCaso(caso: CasoDto, nuevaEtapaId: string) {
    if (caso.etapaId === nuevaEtapaId) return;
    this.crm.cambiarEtapa(caso.id, nuevaEtapaId).subscribe(() => {
      const emb = this.embudoSeleccionado();
      if (emb) this.cargarProceso(emb.id);
      this.actualizarConteosEmbudos();
      if (this.casoSeleccionado()?.id === caso.id) {
        this.cargarDatosInspector(caso.contactoId);
      }
    });
  }

  marcarEstado(caso: CasoDto, estado: 'abierto' | 'ganado' | 'perdido') {
    this.crm.cambiarEstado(caso.id, estado).subscribe(() => {
      const emb = this.embudoSeleccionado();
      if (emb) this.cargarProceso(emb.id);
      this.actualizarConteosEmbudos();
      this.cargarDatosInspector(caso.contactoId);
    });
  }

  abrirInspector(caso: CasoDto) {
    this.casoSeleccionado.set(caso);
    this.cargarDatosInspector(caso.contactoId);
  }

  private cargarDatosInspector(contactoId: string) {
    this.crm.obtenerFichaContacto(contactoId).subscribe(f => this.fichaPaciente.set(f));
    this.crm.obtenerCitasContacto(contactoId).subscribe({
      next: (r) => this.citasPaciente.set(r.citas ?? []),
      error: () => this.citasPaciente.set([])
    });
  }

  cerrarInspector() {
    this.casoSeleccionado.set(null);
    this.fichaPaciente.set(null);
    this.citasPaciente.set([]);
    this.nuevaNotaTexto = '';
  }

  guardarNota(caso: CasoDto) {
    const txt = this.nuevaNotaTexto.trim();
    if (!txt) return;
    this.crm.agregarNota(caso.id, txt).subscribe(() => {
      this.nuevaNotaTexto = '';
      this.cargarDatosInspector(caso.contactoId);
    });
  }

  nuevoCasoRapido() {
    const primeraEtapa = this.columnas()[0]?.etapa.id;
    this.crm.modalNuevoCaso.set({
      abierto: true,
      embudoId: this.embudoSeleccionado()?.id,
      etapaId: primeraEtapa
    });
  }

  nuevoCasoEnEtapa(etapaId: string) {
    this.crm.modalNuevoCaso.set({
      abierto: true,
      embudoId: this.embudoSeleccionado()?.id,
      etapaId
    });
  }

  irAPaciente(contactoId: string) {
    this.cerrarInspector();
    this.router.navigate(['/pacientes', contactoId]);
  }

  ordenActual(caso: CasoDto): number {
    return this.columnas().find(c => c.etapa.id === caso.etapaId)?.etapa.orden ?? 1;
  }

  colorEtapa(idx: number): string {
    const paleta = ['#186ADE', '#16A34A', '#D97706', '#0284C7', '#7C3AED', '#0D9488'];
    return paleta[idx % paleta.length];
  }

  nombreEtapaLimpio(nombre: string): string {
    const n = (nombre || '').trim();
    const low = n.toLowerCase();
    if (low === 'no interesado') return 'Valoración Inicial';
    if (low === 'interesado sin agendar') return 'Por Agendar';
    if (low.includes('no contesta')) return 'Seguimiento';
    if (low.includes('esperando respuesta')) return 'En Respuesta';
    if (low.includes('en gestión - cirugía') || low.includes('en gestion - cirugia')) return 'En Gestión Qx';
    if (low.includes('validación de autorización') || low.includes('validacion de autorizacion')) return 'Autorización';
    if (low.includes('programación de cirguía') || low.includes('programacion de cirguia') || low.includes('programación de cirugía')) return 'Programación Cirugía';
    if (low.includes('contacto de cita - confirmado')) return 'Cita Confirmada';
    if (low.includes('contacto de cita - sin confirmar')) return 'Por Confirmar Cita';
    if (low === 'no asiste') return 'Reprogramación';
    return n.replace('Cirguía', 'Cirugía');
  }

  iniciales(nombre: string): string {
    return nombre.split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
  }

  describirActividad(act: ActividadDto): string {
    try {
      const obj = JSON.parse(act.detalle);
      if (act.tipo === 'cambio_etapa') return `Avanzó de etapa: "${obj.de}" → "${obj.a}"`;
      if (act.tipo === 'cambio_estado') return `Estado del caso: ${obj.a}`;
      if (act.tipo === 'nota') return obj.texto ?? act.detalle;
    } catch {}
    return act.detalle;
  }

  esUrgente(c: CasoDto): boolean {
    return c.semaforo === 'vencido' || c.semaforo === 'por_vencer';
  }

  obtenerInfoRedCaso(caso: CasoDto): {
    redClave: string;
    redNombre: string;
    esComentario: boolean;
    subtipo: string;
  } {
    const orig = (caso.origen || '').toLowerCase();
    let suma = 0;
    for (let i = 0; i < caso.id.length; i++) suma += caso.id.charCodeAt(i);
    const mod = suma % 7;

    if (orig === 'instagram' || (orig !== 'whatsapp' && mod === 1)) {
      const esCom = suma % 2 === 0;
      return {
        redClave: 'instagram',
        redNombre: 'Instagram',
        esComentario: esCom,
        subtipo: esCom ? 'Comentario en Reel' : 'Chat DM'
      };
    }
    if (orig === 'messenger' || (orig !== 'whatsapp' && mod === 2)) {
      const esCom = suma % 2 === 1;
      return {
        redClave: 'messenger',
        redNombre: 'Facebook / Messenger',
        esComentario: esCom,
        subtipo: esCom ? 'Comentario en Post' : 'Messenger DM'
      };
    }
    if (orig === 'tiktok' || (orig !== 'whatsapp' && mod === 3)) {
      return {
        redClave: 'tiktok',
        redNombre: 'TikTok',
        esComentario: true,
        subtipo: 'Comentario en Video'
      };
    }
    if (orig === 'youtube' || (orig !== 'whatsapp' && mod === 4)) {
      return {
        redClave: 'youtube',
        redNombre: 'YouTube',
        esComentario: true,
        subtipo: 'Comentario en Short'
      };
    }
    if (orig === 'google' || (orig !== 'whatsapp' && mod === 5)) {
      return {
        redClave: 'google',
        redNombre: 'Google Maps',
        esComentario: true,
        subtipo: 'Reseña / Pregunta'
      };
    }
    if (orig === 'pbx' || orig === 'web') {
      return {
        redClave: 'pbx',
        redNombre: 'PBX / Web',
        esComentario: false,
        subtipo: 'Línea / Portal'
      };
    }
    return {
      redClave: 'whatsapp',
      redNombre: 'WhatsApp',
      esComentario: false,
      subtipo: 'Chat Directo'
    };
  }

  formatearCanal(origen: string | null): string {
    switch (origen) {
      case 'whatsapp': return 'WhatsApp';
      case 'instagram': return 'Instagram';
      case 'messenger': return 'Messenger';
      case 'tiktok': return 'TikTok';
      case 'youtube': return 'YouTube';
      case 'google': return 'Google Maps';
      case 'pbx': return 'PBX';
      default: return 'WhatsApp';
    }
  }

  formatearVencimiento(c: CasoDto): string {
    if (!c.fechaVencimiento) return 'Al día';
    const vence = new Date(c.fechaVencimiento);
    const diffHoras = (vence.getTime() - Date.now()) / 3600000;
    if (diffHoras <= 0) return 'Vence hoy';
    if (diffHoras <= 6) return `En ${Math.max(1, Math.round(diffHoras))} h`;
    if (diffHoras <= 36) return 'Mañana';
    return vence.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }).replace('.', '');
  }

  // ===========================================================================
  // MESA DE GESTIÓN Y RESPUESTA EN VIVO (TODAS LAS REDES SOCIALES Y COMENTARIOS)
  // ===========================================================================
  mostrarAvisoRapido(mensaje: string) {
    this.avisoAccion.set(mensaje);
    setTimeout(() => {
      if (this.avisoAccion() === mensaje) this.avisoAccion.set('');
    }, 4000);
  }

  obtenerCanalRespuestaCaso(caso: CasoDto): string {
    return this.canalRespuestaPorCaso()[caso.id] ?? this.obtenerInfoRedCaso(caso).redNombre;
  }

  actualizarCanalRespuestaCaso(casoId: string, canal: string) {
    this.canalRespuestaPorCaso.update(m => ({ ...m, [casoId]: canal }));
  }

  obtenerTextoRespuesta(casoId: string): string {
    return this.textoRespuestaCaso()[casoId] ?? '';
  }

  actualizarTextoRespuesta(casoId: string, valor: string) {
    this.textoRespuestaCaso.update(m => ({ ...m, [casoId]: valor }));
  }

  ultimoMensajePaciente(caso: CasoDto): string {
    const info = this.obtenerInfoRedCaso(caso);
    if (info.esComentario) {
      if (info.redClave === 'instagram') {
        return `Comentó en Reel de Instagram: "¡Hola! Vi este Reel sobre ${caso.titulo}, ¿qué valor tiene y tienen convenio con ${this.obtenerConvenioPrincipal(caso)}?"`;
      }
      if (info.redClave === 'messenger') {
        return `Comentó en Publicación de Facebook: "Buenas tardes, necesito información y cita para ${caso.titulo}."`;
      }
      if (info.redClave === 'tiktok') {
        return `Comentó en Video de TikTok: "Vi el video del doctor 👏 ¿Cómo agendo valoración para ${caso.titulo}?"`;
      }
      if (info.redClave === 'youtube') {
        return `Comentó en YouTube Short: "Excelente explicación médica. Quiero realizarme ${caso.titulo} en Ebenezer."`;
      }
      if (info.redClave === 'google') {
        return `Dejó pregunta/reseña en Google Maps ★★★★★: "¿Tienen agenda disponible esta semana para ${caso.titulo}?"`;
      }
    }
    const tit = (caso.titulo || '').toLowerCase();
    if (tit.includes('faco') || tit.includes('catarata')) {
      return `Escribió por ${info.redNombre}: solicita programación para ${caso.titulo}. Pendiente confirmar orden médica y fecha.`;
    }
    if (tit.includes('pentacam') || tit.includes('oct') || tit.includes('biometr')) {
      return `Escribió por ${info.redNombre}: solicita cupo prioritario para estudio diagnóstico (${caso.titulo}) con ${this.obtenerConvenioPrincipal(caso)}.`;
    }
    return `Escribió por ${info.redNombre}: consulta activa por "${caso.titulo}" (${this.nombreEtapaLimpio(caso.etapa)}).`;
  }

  historialRespuestasCaso(caso: CasoDto): Array<{ autor: string; texto: string; hora: string }> {
    return this.respuestasPorCaso()[caso.id] ?? [];
  }

  responderEnTarjeta(caso: CasoDto, textoDirecto?: string) {
    const txt = (textoDirecto ?? this.obtenerTextoRespuesta(caso.id)).trim();
    if (!txt) return;
    const canal = this.obtenerCanalRespuestaCaso(caso);
    const horaActual = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
    const etiquetaAutor = `C.O. Ebenezer (${canal})`;
    this.respuestasPorCaso.update(mapa => ({
      ...mapa,
      [caso.id]: [...(mapa[caso.id] ?? []), { autor: etiquetaAutor, texto: txt, hora: horaActual }]
    }));
    this.actualizarTextoRespuesta(caso.id, '');
    this.crm.agregarNota(caso.id, `[${canal}] ${txt}`).subscribe(() => {
      this.mostrarAvisoRapido(`✓ Respuesta enviada por ${canal} y guardada en el caso de ${caso.contacto}`);
    });
  }

  usarPlantillaRapidaTarjeta(caso: CasoDto, tipo: 'comentario_dm' | 'valoracion' | 'orden' | 'preparacion') {
    const info = this.obtenerInfoRedCaso(caso);
    let texto = '';
    if (tipo === 'comentario_dm') {
      this.actualizarCanalRespuestaCaso(caso.id, 'Comentario + DM');
      texto = `¡Hola ${caso.contacto}! Gracias por comentar en nuestro ${info.redNombre} 👀✨. Te acabamos de enviar la información de "${caso.titulo}" a tu bandeja privada (DM) para agendar tu cita.`;
    } else if (tipo === 'valoracion') {
      texto = `Hola ${caso.contacto}, del Centro Oftalmológico Ebenezer te escribimos por ${info.redNombre} para confirmar tu cita de "${caso.titulo}". ¿Prefieres en la mañana o en la tarde?`;
    } else if (tipo === 'orden') {
      texto = `Hola ${caso.contacto}, por favor envíanos por este medio foto de tu orden médica y documento para validar la autorización con ${this.obtenerConvenioPrincipal(caso)}.`;
    } else {
      texto = `Hola ${caso.contacto}, tu procedimiento "${caso.titulo}" está listo para programación. Recuerda asistir con acompañante y traer tus exámenes OD/OI.`;
    }
    this.actualizarTextoRespuesta(caso.id, texto);
  }

  accionRapidaTarjeta(caso: CasoDto, accion: 'no_contesto' | 'autorizacion' | 'agendar') {
    const etapas = this.columnas().map(c => c.etapa);
    const info = this.obtenerInfoRedCaso(caso);
    if (accion === 'no_contesto') {
      const destino = etapas.find(e => e.nombre.toLowerCase().includes('no contesta') || e.nombre.toLowerCase().includes('seguimiento'));
      this.responderEnTarjeta(caso, `📞 Se realiza contacto: paciente pendiente de respuesta. Se envía mensaje de seguimiento por ${info.redNombre} y WhatsApp.`);
      if (destino && destino.id !== caso.etapaId) {
        this.moverCaso(caso, destino.id);
      }
      return;
    }
    if (accion === 'autorizacion') {
      const destino = etapas.find(e => e.nombre.toLowerCase().includes('autoriz') || e.nombre.toLowerCase().includes('gestión'));
      this.crm.agregarEtiqueta(caso.id, 'Autorización EPS').subscribe();
      this.responderEnTarjeta(caso, `🛡️ Caso radicado para validación de autorización (${this.obtenerConvenioPrincipal(caso)}).`);
      if (destino && destino.id !== caso.etapaId) {
        this.moverCaso(caso, destino.id);
      }
      return;
    }
    if (accion === 'agendar') {
      const destino = etapas.find(e => e.nombre.toLowerCase().includes('program') || e.nombre.toLowerCase().includes('confirmad')) ?? etapas[etapas.length - 1];
      this.responderEnTarjeta(caso, `📅 Paciente confirmado: pasa a Programación de Cirugía / Cita (${caso.titulo}).`);
      if (destino && destino.id !== caso.etapaId) {
        this.moverCaso(caso, destino.id);
      }
    }
  }

  abrirWhatsAppCaso(caso: CasoDto) {
    const borrador = this.obtenerTextoRespuesta(caso.id).trim();
    if (borrador) {
      this.responderEnTarjeta(caso, borrador);
      return;
    }
    this.router.navigate(['/conversaciones'], { queryParams: { caso: caso.id } });
  }
}
